import Button from "components/common/Button";
import CustomTable from "components/common/CustomTable";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import AddressInput from "components/common/InputField/AddressInput";
import SelectInput from "components/common/InputField/SelectInput";
import TextInput from "components/common/InputField/TextInput";
import Loader from "components/common/Loader";
import ModalContainer from "components/common/ModalContainer";
import VendorAvatar from "components/common/VendorAvatar";
import PageFrame from "components/layout/PageFrame";
import { getCurrencyFromCurrencyCode } from "helpers";
import { determineRiskStatus } from "helpers/determineRiskStatus";
import { emailRegex } from "helpers/emailRegex";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import useInfiniteScroll from "hooks/useInfinteScroll";
import { cloneDeep, debounce, truncate, uniqBy } from "lodash";
import React, { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { inventoryActions, vendorActions } from "redux/Ldb/actions";
import { AccountPermissions } from "types/permissions";
import countries from "variables/countries";
import { paymentTerms } from "variables/paymentTerms";
import { supplierCatgories } from "variables/supplierCategories";

const Suppliers = ({
    productDetail,
    inventoryId,
    type,
}: {
    productDetail: Record<string, any>;
    inventoryId?: string;
    type?: string;
}) => {
    const [filter, setFilter] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [debouncedSupplierSearch, setDebouncedSupplierSearch] = useState<string>("");
    const [suppliers, setSuppliers] = useState<{ [key: string]: any }[]>([]);
    const [ldbVendorList, setLdbVendorList] = useState<{ [key: string]: any }[]>([]);
    const [pagination, setPagination] = useState<{ [key: string]: any }>({
        currentPage: "1",
        noOfPages: "",
    });
    const [supplierDetails, setSupplierDetails] = useState<{ [key: string]: any }>({
        mobileExtension: "+1",
    });
    const [topSuppliers, setTopSuppliers] = useState<{ [key: string]: any }[]>([]);
    const [addSupplierModal, setAddSupplierModal] = useState<boolean>(false);
    const [newSupplierModal, setNewSupplierModal] = useState<boolean>(false);
    const [selectedSupplier, setSelectedSupplier] = useState<string>("");
    const [address, setAddress] = useState<{ [key: string]: any }>({});
    const [online, setOnline] = useState<boolean>(false);
    const [additionalEmails, setAdditionalEmails] = useState<string>("");
    const [valueArray, setValueArray] = useState<string[]>([]);
    const [resetAddress, setResetAddress] = useState<boolean>(false);
    const [productData, setProductData] = useState<{ [key: string]: any }[]>([]);
    const [paymentTermsData, setPaymentTermsData] = useState<string[]>([]);
    const [customIndex, setCustomIndex] = useState<number>();
    const [addPaymentOption, setAddPaymentOption] = useState(false);
    const [newPaymentOption, setNewPaymentOption] = useState<string>("");
    const [addressDetails, setAddressDetails] = useState<{ [key: string]: any }>({});
    const [reset, setReset] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { productId: productIdInUrl } = useParams();
    const productId = type === "chat" ? inventoryId : productIdInUrl;

    const { fetchingProductSuppliers, fetchedProductSuppliersSuccess } = useAppSelector(
        (state) => state.inventory
    );
    const {
        fetchingLdbVendor,
        fetchLdbVendorSuccess,
        addVendorToInventorySuccess,
        addingVendorToInventory,
        addVendorSuccess,
        addingVendor,
    } = useAppSelector((state) => state?.vendor);

    const { isLoadingMore, setLoadMore, updatedPage, setUpdatedPage } = useInfiniteScroll(
        { current: pagination.currentPage, number_of_pages: pagination.noOfPages },
        20
    );
    const filteredTopSuppliers = topSuppliers?.filter((supplier) => supplier !== undefined);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    const handleDebouncedChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setUpdatedPage(1);
        setDebouncedSearch(e.target.value);
    }, 1000);

    useEffect(() => {
        setPaymentTermsData(paymentTerms);
    }, []);

    // const areAllValuesEmpty = (obj) => {
    //     return Object.values(obj).every((value) => {
    //         if (typeof value === "object" && value !== null) {
    //             // Recursively check nested objects
    //             return areAllValuesEmpty(value);
    //         }
    //         return value === "";
    //     });
    // };

    // const allObjectsEmpty = productData?.every((obj) => areAllValuesEmpty(obj));

    useEffect(() => {
        if (address?.contactAddress?.address) {
            const cityCountry = address?.contactAddress?.address?.split(",");
            setAddressDetails({
                city: cityCountry[cityCountry?.length - 2],
                country: cityCountry[cityCountry?.length - 1],
            });
        }
    }, [address]);

    const handleMultipleEmails = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAdditionalEmails(value);
    };

    const handleSupplierDetails = (e) => {
        const { name, value } = e.target;
        setSupplierDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCreateNewSupplier = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (additionalEmails && !emailRegex.test(additionalEmails)) {
            toast.custom((t) => (
                <CustomToast t={t} message={"Please enter a valid email address"} type={"error"} />
            ));
            return;
        }

        let body = {
            businessName: supplierDetails?.businessName,
            ...(online && supplierDetails?.website && { website: supplierDetails?.website }),
            ...(!online && {
                mobileExtension: supplierDetails?.mobileExtension,
                email: supplierDetails?.email,
                mobile: supplierDetails?.mobile,
                businessCategory: supplierDetails?.businessCategory,
                ...(valueArray?.length > 0 && { emails: valueArray }),
            }),
            source: online ? "online" : "offline",
            ...(((online && supplierDetails?.firstName) || !online) && {
                firstName: supplierDetails?.firstName,
            }),
            ...(((online && supplierDetails?.lastName) || !online) && {
                lastName: supplierDetails?.lastName,
            }),
            ...(((online && address?.contactAddress?.lat) || !online) && {
                address: {
                    lat: address?.contactAddress?.lat,
                    lng: address?.contactAddress?.lng,
                    address: address?.contactAddress?.address,
                    country: addressDetails?.country?.trim(),
                    city: addressDetails?.city?.trim() || addressDetails?.country?.trim(),
                },
            }),
            ...(productData?.length > 0 && { products: productData }),
        };

        if (additionalEmails && emailRegex.test(additionalEmails) && !online) {
            if (!valueArray?.includes(additionalEmails)) {
                body = {
                    ...body,
                    emails: [...valueArray, additionalEmails],
                };
            }
        }

        dispatch(vendorActions.addVendor(body));
    };

    const handleProductDataChange = (e, idx) => {
        const { name, value } = e.target;
        const copyProductData = [...productData];
        if (name === "value" || name === "duration") {
            copyProductData[idx] = {
                ...copyProductData[idx],
                leadTime: {
                    ...copyProductData[idx]?.leadTime,
                    [name]: value,
                },
            };
        } else {
            copyProductData[idx][name] = value;
        }

        setProductData(copyProductData);
    };

    const handleAddSupplier = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        dispatch(
            vendorActions.addVendorToInventory({
                inventoryId: productId,
                vendors: [selectedSupplier],
            })
        );
    };

    useEffect(() => {
        if (Boolean(addVendorSuccess)) {
            dispatch(vendorActions?.resetAddVendorSuccess());
            setSupplierDetails({});
            setAddress({});
            setProductData([{}]);
            setAddressDetails({});
            setResetAddress(true);
            setValueArray([]);
            setAdditionalEmails("");
            setAddSupplierModal(false);
            setNewSupplierModal(false);
            setUpdatedPage(1);
            setReset(!reset);
        }
    }, [dispatch, addVendorSuccess]);

    useEffect(() => {
        if (Boolean(addVendorToInventorySuccess)) {
            setAddSupplierModal(false);
            setUpdatedPage(1);
            setReset(!reset);
            dispatch(vendorActions.resetAddVendorToInventory());
        }
    }, [dispatch, addVendorToInventorySuccess]);

    useEffect(() => {
        dispatch(
            inventoryActions.fetchProductSuppliers(
                15,
                updatedPage,
                productId,
                filter === "all" ? "" : filter,
                debouncedSearch
            )
        );

        return () => {
            dispatch(inventoryActions.resetFetchProductSuppliers());
        };
    }, [dispatch, filter, productId, debouncedSearch, updatedPage, reset]);

    useEffect(() => {
        if (Boolean(fetchedProductSuppliersSuccess)) {
            const parsedSupplier = cloneDeep(fetchedProductSuppliersSuccess);
            setSuppliers((prev) =>
                updatedPage === 1
                    ? [...parsedSupplier?.suppliers]
                    : [...prev, ...parsedSupplier?.suppliers]
            );
            setTopSuppliers([
                parsedSupplier?.topSupplier?.[0],
                parsedSupplier?.leastRiskSupplier?.[0],
            ]);
            setPagination({
                currentPage: parsedSupplier.pagination?.current,
                noOfPages: parsedSupplier.pagination?.number_of_pages,
            });
            setLoadMore(false);
        }
    }, [fetchedProductSuppliersSuccess, updatedPage]);

    useEffect(() => {
        if (Boolean(fetchLdbVendorSuccess)) {
            setLdbVendorList((prev) => [...fetchLdbVendorSuccess]);
        }
    }, [fetchLdbVendorSuccess]);

    useEffect(() => {
        dispatch(vendorActions?.fetchVendors(debouncedSupplierSearch, null, 15, 1));
    }, [dispatch, debouncedSupplierSearch]);

    const suppliersTableHeader = [
        { title: "Supplier", widthClass: "w-[17.4%]" },
        { title: "Contact person", widthClass: "w-[11.4%]" },
        { title: "Email address", widthClass: "w-[18.5%]" },
        { title: "Phone number", widthClass: "w-[11.4%]" },
        { title: "Lead time", widthClass: "w-[7.6%]" },
        { title: "Risk status", widthClass: "w-[11.5%]" },
        { title: "Address", widthClass: "w-[22.2%]" },
    ];

    const suppliersTableBody = suppliers?.map((supplier, idx) => [
        {
            content: (
                <div
                    className={`py-3 pr-3 flex font-rocGroteskMedium items-center h-full border-slate-100 text-sm text-slate-800 `}
                >
                    <div className="flex items-center space-x-2">
                        <VendorAvatar
                            imageSrc={supplier?.vendor?.profile?.avatar}
                            size={32}
                            name={supplier?.vendor?.profile?.businessName}
                            containerClassname="!rounded-md"
                        />
                        <span>
                            {truncate(supplier?.vendor?.profile?.businessName, { length: 15 })}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 whitespace-nowrap`}
                >
                    <span>
                        {(supplier?.vendor?.lastName || "") +
                            " " +
                            (supplier?.vendor?.firstName || "")}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 whitespace-nowrap`}
                >
                    <span>{supplier?.vendor?.email}</span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>{`${supplier?.vendor?.mobileExtension ?? ""}${
                        supplier?.vendor?.mobile ?? ""
                    }`}</span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>{supplier?.leadTime + " days"}</span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <div className="text-sm font-rocGroteskMedium flex items-center space-x-3">
                        {/* <i className="ri-information-line text-sm text-r-50"></i> */}
                        {/* <i className="ri-bar-chart-2-fill text-base text-r-50 "></i> */}
                        <img
                            src={determineRiskStatus(supplier?.vendor?.riskDetails?.riskScore)?.img}
                            className="h-[18px] w-[24px]"
                            alt=""
                        />
                        <span>
                            {determineRiskStatus(supplier?.vendor?.riskDetails?.riskScore)?.value}{" "}
                            risk
                        </span>
                    </div>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>{supplier?.vendor?.profile?.address?.address}</span>
                </div>
            ),
        },
    ]);

    useEffect(() => {
        setProductData([
            {
                productName: productDetail?.productName,
                productCategory: productDetail?.productCategory,
                unitPrice: productDetail?.unitCost?.amount,
                leadTime: {
                    duration: "",
                    value: "",
                },
                discount: "",
                paymentTerms: "",
                inventoryId: productDetail?._id,
                currency: productDetail?.unitCost?.currency,
            },
        ]);
    }, [productDetail]);

    const prodTableHeader = [
        { title: "Product name", widthClass: "w-[20%] !font-rocGroteskMedium" },
        {
            title: (
                <div className="flex items-center space-x-1.5">
                    <span className="">Unit cost</span>
                    <span className="required text-r-50 text-sm mt-[-2px] font-rocGroteskMedium">
                        *
                    </span>
                </div>
            ),
            widthClass: "w-[12%] !font-rocGroteskMedium",
        },
        {
            title: (
                <div className="flex items-center space-x-1.5">
                    <span className="">Lead time duration</span>
                    <span className="required text-r-50 text-sm mt-[-2px] font-rocGroteskMedium">
                        *
                    </span>
                </div>
            ),
            widthClass: "w-[20%] !font-rocGroteskMedium",
        },
        {
            title: (
                <div className="flex items-center space-x-1.5">
                    <span className="">Lead time value</span>
                    <span className="required text-r-50 text-sm mt-[-2px] font-rocGroteskMedium">
                        *
                    </span>
                </div>
            ),
            widthClass: "w-[12%] !font-rocGroteskMedium",
        },
        {
            title: (
                <div className="flex items-center space-x-0.5">
                    <span className="">Discount</span>
                    <span className="required text-r-50 text-sm mt-[-2px] font-rocGroteskMedium">
                        *
                    </span>
                </div>
            ),
            widthClass: "w-[12%] !font-rocGroteskMedium",
        },
        {
            title: (
                <div className="flex items-center space-x-0.5">
                    <span className="">Payment terms</span>
                    <span className="required text-r-50 text-sm mt-[-2px] font-rocGroteskMedium">
                        *
                    </span>
                </div>
            ),
            widthClass: "w-[20%] !font-rocGroteskMedium",
        },
    ];

    const prodTableBody = productData?.map((item, idx) => {
        return [
            {
                content: (
                    <div className={`w-full  border-slate-100 text-sm text-slate-700 `}>
                        <p className="px-2 font-rocGroteskMedium text-[13px]">
                            {truncate(item?.productName, { length: 25 })}
                        </p>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`w-full border-slate-100 text-sm text-slate-700 flex items-center px-2.5`}
                    >
                        <p className="">
                            {item?.currency ? getCurrencyFromCurrencyCode(item?.currency) : "$"}
                        </p>
                        <TextInput
                            value={item?.unitPrice ?? ""}
                            name={"unitPrice"}
                            required={item?.inventoryId ? true : false}
                            onChange={(e) => handleProductDataChange(e, idx)}
                            inputPlaceholder="0"
                            containerClassname=""
                            inputContainerClassname={"!border-none !rounded-none "}
                            inputClassName="w-full !px-0 !py-0"
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },

            {
                content: (
                    <div className={`w-full  border-slate-100 text-sm text-slate-700 `}>
                        <SelectInput
                            value={item?.leadTime?.duration || ""}
                            name="duration"
                            inputPlaceholder={"Select"}
                            handleChange={(name, value) =>
                                handleProductDataChange({ target: { name, value } }, idx)
                            }
                            isRequired={item?.inventoryId ? true : false}
                            className="!border-0 !rounded-none"
                            inputContainerClassName="!mb-0"
                            clearValue={true}
                            dropdownClassName="!max-h-[100px]"
                            optionItemClassName="!text-xs !py-1"
                            optionItemContainerClassName="!px-2"
                            dropdownOptions={[
                                {
                                    label: "Hours",
                                    value: "hours",
                                },
                                {
                                    label: "Days",
                                    value: "days",
                                },
                                {
                                    label: "Weeks",
                                    value: "weeks",
                                },
                                {
                                    label: "Months",
                                    value: "months",
                                },
                            ]}
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },
            {
                content: (
                    <div className={`w-full border-slate-100 text-sm text-slate-700 px-2.5`}>
                        <TextInput
                            value={item?.leadTime?.value ?? ""}
                            name="value"
                            type={"number"}
                            onWheel={(e: any) => e.target.blur()}
                            onKeyDown={(evt) =>
                                ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(evt.key) &&
                                evt.preventDefault()
                            }
                            required={item?.inventoryId ? true : false}
                            onChange={(e) => handleProductDataChange(e, idx)}
                            inputPlaceholder="0"
                            containerClassname=""
                            inputContainerClassname={"!border-none !rounded-none "}
                            inputClassName="w-full "
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`w-full border-slate-100 text-sm text-slate-700 px-2.5 flex items-center`}
                    >
                        <div className="!w-[50px]">
                            <TextInput
                                value={item?.discount ?? ""}
                                name={"discount"}
                                type={"number"}
                                onWheel={(e: any) => e.target.blur()}
                                onKeyDown={(evt) =>
                                    ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(
                                        evt.key
                                    ) && evt.preventDefault()
                                }
                                required={item?.inventoryId ? true : false}
                                onChange={(e) => handleProductDataChange(e, idx)}
                                inputPlaceholder="0"
                                containerClassname=""
                                inputContainerClassname={"!border-none !rounded-none"}
                                inputClassName=" "
                            />
                        </div>
                        <p className="">%</p>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },
            {
                content: (
                    <div className={`w-full border-slate-100 text-sm text-slate-700 `}>
                        <SelectInput
                            value={item?.paymentTerms || ""}
                            name="paymentTerms"
                            handleChange={(name, value) =>
                                handleProductDataChange({ target: { name, value } }, idx)
                            }
                            inputPlaceholder={"Select"}
                            isRequired={item?.inventoryId ? true : false}
                            optionItemClassName="!text-xs !py-1"
                            optionItemContainerClassName="!px-2"
                            dropdownOptions={paymentTermsData?.map((data) => ({
                                label: data,
                                value: data,
                            }))}
                            className="!border-0 !rounded-none"
                            dropdownClassName="!pb-0 !max-h-[100px]"
                            rightDropdownContainerClassName="!sticky bottom-0"
                            customRightDropdownContainer={
                                <div className="py-1 px-1  flex items-center bg-white !h-[48px] sticky w-full bottom-0">
                                    <div
                                        className="flex items-center w-fit cursor-pointer"
                                        onClick={() => {
                                            setAddPaymentOption(true);
                                            setCustomIndex(idx);
                                        }}
                                    >
                                        <i className="ri-add-fill text-[#0046A2] text-[10px]"></i>

                                        <span className="text-[12px] font-rocGroteskMedium text-[#0046A2] underline">
                                            Add custom options
                                        </span>
                                    </div>
                                </div>
                            }
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },
            // {
            //     content: (
            //         <div className={`w-full border-slate-100 text-sm text-slate-500 `}>
            //             <i
            //                 className={`ri-delete-bin-line text-r-55`}
            //                 //    onClick={() => handleDeleteProduct(idx)}
            //             ></i>
            //         </div>
            //     ),
            // },
        ];
    });
    return (
        <PageFrame>
            <div>
                <div className="pb-6">
                    {filteredTopSuppliers?.length > 0 && (
                        <div className="mb-8">
                            <div className="flex items-center space-x-2.5 mb-4">
                                <h5 className="text-xl font-rocGroteskMedium text-g-75">
                                    Top suppliers
                                </h5>
                            </div>

                            <div className="grid grid-cols-4 space-x-3">
                                {topSuppliers?.[0] && (
                                    <div className="p-4 rounded-[6px] border border-slate-200 shadow-boxShadow-12">
                                        <div className="mb-1 flex items-center space-x-1">
                                            <VendorAvatar
                                                size={32}
                                                name={
                                                    topSuppliers?.[0]?.vendorProfile?.businessName
                                                }
                                                imageSrc={topSuppliers?.[0]?.vendorProfile?.avatar}
                                                textClassname=""
                                            />
                                        </div>
                                        <span className="block text-base font-rocGroteskMedium text-g-75">
                                            {truncate(
                                                topSuppliers?.[0]?.vendorProfile?.businessName,
                                                {
                                                    length: 25,
                                                }
                                            )}
                                        </span>
                                        <span className="block text-sm font-rocGroteskMedium text-slate-500 mb-2">
                                            {truncate(
                                                topSuppliers?.[0]?.vendorProfile?.address?.address,
                                                {
                                                    length: 30,
                                                }
                                            )}
                                        </span>
                                        <div className="flex items-center space-x-1 px-1.5 h-6 rounded-[3px] bg-[#F0FDF4] w-fit">
                                            <i className="ri-arrow-up-line text-sm text-[#22C55E] mt-[-2px]"></i>
                                            <span className="text-sm text-[#22C55E] font-rocGroteskMedium">
                                                {topSuppliers?.[0]?.leadTime} Days
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {topSuppliers?.[1] && (
                                    <div className="p-4 rounded-[6px] border border-slate-200 shadow-boxShadow-12">
                                        <div className="mb-1 flex items-center space-x-1">
                                            <VendorAvatar
                                                size={32}
                                                name={
                                                    topSuppliers?.[1]?.vendorProfile?.businessName
                                                }
                                                imageSrc={topSuppliers?.[1]?.vendorProfile?.avatar}
                                                textClassname=""
                                            />
                                        </div>
                                        <span className="block text-base font-rocGroteskMedium text-g-75">
                                            {truncate(
                                                topSuppliers?.[1]?.vendorProfile?.businessName,
                                                {
                                                    length: 25,
                                                }
                                            )}
                                        </span>
                                        <span className="block text-sm font-rocGroteskMedium text-slate-500 mb-2">
                                            {truncate(
                                                topSuppliers?.[1]?.vendorProfile?.address?.address,
                                                {
                                                    length: 30,
                                                }
                                            )}
                                        </span>
                                        <div className="flex items-center space-x-1 px-1.5 h-6 rounded-[3px] border border-slate-200 w-fit">
                                            <i className="ri-bar-chart-2-fill text-sm text-slate-500 mt-[-2px]"></i>
                                            <span className="text-sm text-slate-500 font-rocGroteskMedium">
                                                {
                                                    determineRiskStatus(
                                                        topSuppliers?.[1]?.riskDetails?.riskScore
                                                    )?.value
                                                }{" "}
                                                risk
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <div className="flex items-center justify-end mb-4">
                            {/* <h5 className="text-xl font-rocGroteskMedium text-g-75">
                                Suppliers
                            </h5> */}

                            <div className="flex items-center space-x-3">
                                <TextInput
                                    name={"search"}
                                    value={search}
                                    type={"text"}
                                    inputPlaceholder={"Search supplier, Date, Status"}
                                    inputClassName={"!h-[38px] pl-[0px] !bg-white text-sm !mb-0"}
                                    onChange={handleSearch}
                                    onInput={handleDebouncedChange}
                                    leftIcon={
                                        <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                                    }
                                    inputContainerClassname={
                                        "!rounded-[4px] !w-[400px] max-sm:!w-full !pl-[40px] !h-[40px] !bg-white !border !border-n-20"
                                    }
                                    containerClassname={"!w-fit"}
                                />

                                <Dropdown
                                    handleChange={(name, value) => {
                                        setUpdatedPage(1);
                                        setFilter(value);
                                    }}
                                    dropdown={
                                        <div className="flex items-center justify-between whitespace-nowrap h-[40px] w-[237px] py-2 px-4 space-x-1 rounded-[5px] border border-n-20">
                                            <div className="flex items-center space-x-1">
                                                <i className="ri-bar-chart-2-fill text-sm text-g-75 "></i>
                                                <span
                                                    className={`text-g-75 font-rocGroteskMedium text-[13px] capitalize`}
                                                >
                                                    {filter} risk supplier
                                                </span>
                                            </div>
                                            <i className="ri-arrow-down-s-line text-g-75"></i>
                                        </div>
                                    }
                                    // dropdownItemsClasses={`!px-0 !py-0`}
                                    // dropdownClassname={"!w-full !mb-10 !-mt-7"}
                                    dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100  rounded !max-h-fit`}
                                    dropdownOptions={[
                                        {
                                            label: "All",
                                            value: "all",
                                        },
                                        {
                                            label: "High risk supplier",
                                            value: "high",
                                        },
                                        {
                                            label: "Medium risk supplier",
                                            value: "medium",
                                        },
                                        {
                                            label: "Low risk supplier",
                                            value: "low",
                                        },
                                    ]}
                                />

                                <Button
                                    btnClassname="px-4 py-2 !w-[114px] !h-10 whitespace-nowrap"
                                    btnText="Add Supplier"
                                    btnPermission={[
                                        AccountPermissions.all_access,
                                        AccountPermissions.create_suppliers,
                                    ]}
                                    onClick={() => {
                                        setAddSupplierModal(true);
                                        // navigate("/dashboard/suppliers/create-supplier", {
                                        //     state: { internalNavigation: true },
                                        // });
                                    }}
                                />
                            </div>
                        </div>
                        {suppliers.length > 0 ? (
                            <div>
                                <CustomTable
                                    tableHeader={suppliersTableHeader}
                                    tableBody={suppliersTableBody}
                                    isScrollable={false}
                                    isCellBordered
                                    isCellSelectable={false}
                                    isAllSelectable={false}
                                    headerContainerClass="!bg-slate-50"
                                    headerItemClass="font-rocGroteskMedium "
                                    tableClass="mb-6"
                                />

                                {fetchingProductSuppliers && isLoadingMore && (
                                    <div className="flex justify-center my-4">
                                        <Loader color="gm-25" size={4} />
                                    </div>
                                )}
                                {pagination?.currentPage === pagination?.noOfPages && (
                                    <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                                        End of list
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <div className="flex justify-center items-center h-[300px]">
                                    <span className="text-g-75 font-rocGroteskMedium text-lg">
                                        No supplier data available
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ModalContainer
                open={addSupplierModal}
                showCloseIcon={false}
                tailwindClassName="w-[50%] max-h-[80vh]"
                closeModal={() => {
                    setAddSupplierModal(false);
                }}
            >
                <form
                    onSubmit={(e) => handleAddSupplier(e)}
                    className="bg-white rounded-lg shadow-cardShadow relative"
                >
                    <div className="">
                        <div className="px-6 flex justify-between py-4 items-center border-b border-slate-200">
                            <p className="text-lg font-rocGroteskMedium text-g-75">
                                Add a supplier
                            </p>
                            <i
                                className="ri-close-fill text-xl text-gm-50 cursor-pointer"
                                onClick={() => {
                                    setAddSupplierModal(false);
                                }}
                            ></i>
                        </div>

                        <div className="px-6 pt-[18px] mb-16">
                            <div>
                                <h4 className="text-base font-rocGroteskMedium mb-4">
                                    Select an existing supplier
                                </h4>
                                <SelectInput
                                    value={selectedSupplier}
                                    name="supplier"
                                    placeholder={"Select supplier"}
                                    handleChange={(_, value) => setSelectedSupplier(value)}
                                    handleInputChange={debounce((evt) => {
                                        setDebouncedSupplierSearch(evt.target.value);
                                    }, 800)}
                                    isRequired={true}
                                    searchLoading={fetchingLdbVendor}
                                    dropdownOptions={ldbVendorList?.map((item) => ({
                                        label: item?.vendorData?.profile?.businessName,
                                        value: item?.vendorId,
                                    }))}
                                />
                            </div>

                            <div className="flex items-center justify-center space-x-6 py-6">
                                <div className="w-full border-t border-dashed border-slate-200" />
                                <span className="text-slate-500 font-rocGroteskMedium text-sm">
                                    or
                                </span>
                                <div className="w-full border-t border-dashed border-slate-200" />
                            </div>

                            <div className="px-6 py-8 border border-dashed border-slate-200 rounded flex flex-col items-center justify-center space-y-4">
                                <i className="ri-store-2-line text-slate-500 text-4xl"></i>
                                <span className="block text-sm font-rocGroteskMedium ">
                                    Add a new supplier
                                </span>

                                <Button
                                    btnText={"Add supplier"}
                                    type={"button"}
                                    btnClassname="!py-2 !bg-transparent !text-dark-gray-2 !px-4 !border !border-n-40 !w-fit"
                                    onClick={() => {
                                        setAddSupplierModal(false);
                                        setNewSupplierModal(true);
                                        setSelectedSupplier("");
                                    }}
                                    icon={<i className="ri-add-line text-sm"></i>}
                                />
                            </div>
                        </div>

                        <div className="w-full space-x-3 px-6 flex border-t border-slate-200 py-3">
                            <Button
                                btnText={"Close"}
                                type={"button"}
                                btnClassname="!py-3 !bg-n-20  !text-dark-gray-2 !px-4"
                                onClick={() => {
                                    setAddSupplierModal(false);
                                }}
                            />
                            <Button
                                btnText={"Add Supplier"}
                                isLoading={addingVendorToInventory}
                                type={"submit"}
                                btnPermission={[
                                    AccountPermissions.all_access,
                                    AccountPermissions.create_suppliers,
                                ]}
                            />
                        </div>
                    </div>
                </form>
            </ModalContainer>

            <ModalContainer
                open={newSupplierModal}
                showCloseIcon={false}
                tailwindClassName="w-[90%] lg:w-[700px] max-w-[700px] max-h-[90vh]"
                closeModal={() => {
                    setNewSupplierModal(false);
                }}
            >
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateNewSupplier(e);
                    }}
                    className="bg-white rounded-lg shadow-cardShadow relative"
                >
                    <div className="">
                        <div className="px-6 flex justify-between py-4 items-center border-b border-slate-200">
                            <p className="text-lg font-rocGroteskMedium text-g-75">
                                Add a supplier
                            </p>
                            <i
                                className="ri-close-fill text-xl text-gm-50 cursor-pointer"
                                onClick={() => {
                                    setNewSupplierModal(false);
                                }}
                            ></i>
                        </div>

                        <div className="px-6 pt-[18px] mb-6 max-h-[66vh] overflow-y-auto">
                            <div className="mb-5">
                                <p className="text-base font-rocGroteskMedium ">
                                    This is an online supplier
                                </p>
                                <div className="flex items-center">
                                    <div
                                        className="mr-5 cursor-pointer flex items-center"
                                        onClick={() => {
                                            setOnline(true);
                                            setAdditionalEmails("");
                                            setValueArray([]);
                                        }}
                                    >
                                        {online ? (
                                            <i
                                                className={`ri-radio-button-fill text-xl ${
                                                    online && "text-y-10"
                                                }`}
                                            ></i>
                                        ) : (
                                            <span className={`material-icons text-xl text-n-50`}>
                                                radio_button_unchecked
                                            </span>
                                        )}
                                        <span className="text-sm font-rocGroteskMedium ml-[5px]">
                                            Yes
                                        </span>
                                    </div>

                                    <div
                                        className="cursor-pointer flex items-center"
                                        onClick={() => {
                                            setOnline(false);
                                            setAdditionalEmails("");
                                            setValueArray([]);
                                        }}
                                    >
                                        {!online ? (
                                            <i
                                                className={`ri-radio-button-fill text-xl ${
                                                    !online && "text-y-10"
                                                }`}
                                            ></i>
                                        ) : (
                                            <span className={`material-icons text-xl text-n-50`}>
                                                radio_button_unchecked
                                            </span>
                                        )}
                                        <span className="text-sm font-rocGroteskMedium ml-[5px] translate-y-[1px]">
                                            No
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="mb-4">
                                    <h4 className="text-base font-rocGroteskMedium mb-1">
                                        Supplier Information<span className="text-r-50">*</span>
                                    </h4>
                                    <p className="text-slate-500 text-sm">
                                        These details are mandatory when onboarding a supplier
                                    </p>
                                </div>

                                <div className="grid lg:grid-cols-2 gap-2 ">
                                    <TextInput
                                        name={"businessName"}
                                        type={"text"}
                                        value={supplierDetails?.businessName || ""}
                                        onChange={handleSupplierDetails}
                                        placeholder={"Supplier name"}
                                        required={true}
                                    />

                                    {online && (
                                        <TextInput
                                            name={"website"}
                                            type={"text"}
                                            value={supplierDetails?.website || ""}
                                            onChange={handleSupplierDetails}
                                            placeholder={"Website"}
                                        />
                                    )}
                                    {!online && (
                                        <TextInput
                                            name={"email"}
                                            type={"email"}
                                            value={supplierDetails?.email || ""}
                                            onChange={handleSupplierDetails}
                                            placeholder={"Email address"}
                                            required={true}
                                        />
                                    )}

                                    {!online && (
                                        <div className="w-full grid grid-cols-[115px_calc(100%-119px)] gap-1">
                                            <SelectInput
                                                value={supplierDetails?.mobileExtension || ""}
                                                name="mobileExtension"
                                                placeholder={"Ext"}
                                                handleChange={(name, value) => {
                                                    handleSupplierDetails({
                                                        target: { name, value },
                                                    });
                                                }}
                                                isRequired={true}
                                                inputClassName={
                                                    "!placeholder:text-slate-400 !placeholder:font-rocGroteskMedium"
                                                }
                                                clearValue={true}
                                                dropdownOptions={countries
                                                    .filter((data) => Boolean(data?.codes[0]))
                                                    .sort((p1, p2) =>
                                                        p1.codes[0] > p2.codes[0]
                                                            ? 1
                                                            : p1.codes[0] < p2.codes[0]
                                                            ? -1
                                                            : 0
                                                    )
                                                    .map((item) => ({
                                                        label: item.codes[0]?.replace(" ", ""),
                                                        value: item.codes[0]?.replace(" ", ""),
                                                        icon: (
                                                            <ReactCountryFlag
                                                                countryCode={item?.abbreviation}
                                                                svg
                                                                cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                                                cdnSuffix="svg"
                                                                style={{
                                                                    width: "16px",
                                                                    height: "16px",
                                                                    borderRadius: "50%",
                                                                    marginTop: "-4px",
                                                                }}
                                                            />
                                                        ),
                                                    }))}
                                            />
                                            <TextInput
                                                name={"mobile"}
                                                type={"number"}
                                                value={supplierDetails?.mobile || ""}
                                                placeholder={"Phone number"}
                                                onChange={handleSupplierDetails}
                                                min={5}
                                                onKeyDown={(evt) =>
                                                    [
                                                        "e",
                                                        "E",
                                                        "+",
                                                        "-",
                                                        "ArrowUp",
                                                        "ArrowDown",
                                                    ].includes(evt.key) && evt.preventDefault()
                                                }
                                                required={true}
                                                pattern={"^[0-9]+$"}
                                                title={"must be digits"}
                                                inputClassName={
                                                    "!placeholder:text-slate-400 !placeholder:font-rocGroteskMedium"
                                                }
                                            />
                                        </div>
                                    )}

                                    {!online && (
                                        <SelectInput
                                            value={supplierDetails?.businessCategory || ""}
                                            name="businessCategory"
                                            placeholder={"Select supplier category"}
                                            handleInputChange={debounce((evt) => {}, 800)}
                                            searchLoading={false}
                                            handleChange={(name, value) => {
                                                handleSupplierDetails({ target: { name, value } });
                                            }}
                                            isRequired={true}
                                            dropdownOptions={supplierCatgories?.map((res) => ({
                                                label: res?.label,
                                                value: res?.value,
                                            }))}
                                            clearValue={true}
                                        />
                                    )}

                                    {!online && (
                                        <div className="space-y-2">
                                            <TextInput
                                                name={"additionalEmails"}
                                                type={"email"}
                                                value={additionalEmails}
                                                placeholder={"Additional email addresses"}
                                                onChange={handleMultipleEmails}
                                                setValueArray={setValueArray}
                                                valueArray={valueArray}
                                                onKeyDown={(evt) => {
                                                    if (["Enter", ",", " "].includes(evt.key)) {
                                                        evt.preventDefault();
                                                        if (
                                                            emailRegex.test(additionalEmails) &&
                                                            !valueArray.includes(additionalEmails)
                                                        ) {
                                                            setValueArray([
                                                                ...valueArray,
                                                                additionalEmails,
                                                            ]);
                                                            setAdditionalEmails("");
                                                        } else {
                                                            toast.custom((t) => (
                                                                <CustomToast
                                                                    t={t}
                                                                    message={
                                                                        "Please enter a valid email address "
                                                                    }
                                                                    type={"error"}
                                                                />
                                                            ));
                                                        }
                                                    }
                                                }}
                                                autoCompleteOff={true}
                                                inputClassName={
                                                    "!placeholder:text-slate-400 !placeholder:font-rocGroteskMedium"
                                                }
                                                inputContainerClassname="!min-h-[50px] !h-auto"
                                            />
                                            <p className="text-gm-45 text-[8px] font-rocGroteskMedium">
                                                Click “Enter” or comma (,) to add multiple
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="w-full border-t border-dashed border-slate-200 my-6" />

                            <div>
                                <div className="mb-4">
                                    <h4 className="text-base font-rocGroteskMedium mb-1">
                                        Contact person Information
                                    </h4>
                                    <p className="text-slate-500 text-sm">
                                        This is the supplier representative
                                    </p>
                                </div>

                                <div className="grid lg:grid-cols-2 gap-2 ">
                                    <TextInput
                                        name={"firstName"}
                                        type={"text"}
                                        value={supplierDetails?.firstName || ""}
                                        onChange={handleSupplierDetails}
                                        placeholder={"First name"}
                                        required={online ? false : true}
                                    />

                                    <TextInput
                                        name={"lastName"}
                                        type={"text"}
                                        value={supplierDetails?.lastName || ""}
                                        onChange={handleSupplierDetails}
                                        placeholder={"Last name"}
                                        required={online ? false : true}
                                    />
                                </div>

                                <div className="pb-4 mt-4">
                                    <div className="">
                                        <AddressInput
                                            setData={setAddress}
                                            savedAddress={
                                                address?.contactAddress
                                                    ? address?.contactAddress?.address
                                                    : ""
                                            }
                                            placeholder="Address"
                                            required={online ? false : true}
                                            name={"contactAddress"}
                                            resetLocation={resetAddress}
                                            setResetLocation={setResetAddress}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pb-4 w-full mt-4">
                                <p className="text-sm lg:text-base text-gm-50 font-rocGroteskMedium lg:mb-2 flex items-center space-x-1.5">
                                    <span>Product details</span>{" "}
                                    <span className="text-xs text-p-50 px-1.5 rounded-xl border border-p-50 py-[2px]">
                                        Optional
                                    </span>
                                </p>
                                {productData?.length > 0 && (
                                    <div className="">
                                        <div className="">
                                            <CustomTable
                                                tableBody={prodTableBody}
                                                tableHeader={prodTableHeader}
                                                isScrollable={false}
                                                isCellBordered
                                                isCellSelectable={false}
                                                isAllSelectable={false}
                                                scrollWithin={false}
                                                headerContainerClass="!bg-slate-100"
                                                headerItemClass="font-rocGroteskMedium "
                                                tableClass=""
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center gap-3 mt-6">
                                    <Button
                                        btnText="Add more products"
                                        btnPermission={[
                                            AccountPermissions.all_access,
                                            AccountPermissions.create_suppliers,
                                        ]}
                                        btnTextClassName="underline"
                                        btnClassname="text-[13px] !px-0 lg:!px-2.5 !bg-transparent lg:!bg-[rgba(0,56,129,0.06)] !w-fit text-slate-500 lg:!text-[rgba(0,56,129,1)] !rounded-[4px] "
                                        disabled={true}
                                        icon={
                                            <i className="lg:ri-add-circle-line text-[rgba(0,56,129,1)]"></i>
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="w-full space-x-3 px-6 flex border-t border-slate-200 py-3">
                            <Button
                                btnText={"Close"}
                                type={"button"}
                                btnClassname="!py-3 !bg-n-20  !text-dark-gray-2 !px-4"
                                onClick={() => {
                                    setNewSupplierModal(false);
                                }}
                            />
                            <Button
                                btnText={"Add Supplier"}
                                type={"submit"}
                                btnPermission={[
                                    AccountPermissions.all_access,
                                    AccountPermissions.create_suppliers,
                                ]}
                                isLoading={addingVendor}
                            />
                        </div>
                    </div>
                </form>
            </ModalContainer>

            <ModalContainer
                open={addPaymentOption}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[488px]"
                showCloseIcon={false}
                closeModal={() => {
                    setAddPaymentOption(false);
                }}
            >
                <div className="bg-white !px-2 pt-6 pb-7  rounded-[12px]">
                    <div className="px-6">
                        <div className="text-start flex  flex-col  space-y-5">
                            <h3 className="text-xl text-[#333333] font-rocGroteskMedium">
                                Add custom options
                            </h3>
                            <TextInput
                                placeholder="Enter Option"
                                value={newPaymentOption}
                                onChange={(e) => setNewPaymentOption(e.target.value)}
                            />
                        </div>
                        <div className="flex justify-center lg:justify-end">
                            <div className="mt-5 flex justify-center lg:justify-end">
                                <Button
                                    btnText={"Add payment options"}
                                    btnClassname="!py-3 cursor-pointer !text-[14px]  !font-rocGroteskMedium !px-4"
                                    onClick={() => {
                                        if (newPaymentOption.trim() !== "") {
                                            paymentTermsData?.push(newPaymentOption);
                                            const copyData = [...productData];

                                            copyData[customIndex] = {
                                                ...copyData[customIndex],
                                                paymentTerms: newPaymentOption,
                                            };
                                            setProductData(copyData);
                                            setAddPaymentOption(false);
                                            setNewPaymentOption("");
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ModalContainer>
        </PageFrame>
    );
};

export default Suppliers;
